import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CONTACT } from "../data";

function Field({ id, label, type = "text", value, onChange, textarea }) {
  return (
    <div className="relative">
      {textarea ? (
        <textarea
          id={id}
          rows={4}
          value={value}
          onChange={onChange}
          placeholder=" "
          data-testid={`contact-${id}`}
          className="peer w-full bg-transparent border-b border-line py-3 text-base text-charcoal outline-none focus:border-charcoal transition-colors resize-none"
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          placeholder=" "
          data-testid={`contact-${id}`}
          className="peer w-full bg-transparent border-b border-line py-3 text-base text-charcoal outline-none focus:border-charcoal transition-colors"
        />
      )}
      <label
        htmlFor={id}
        className="pointer-events-none absolute left-0 top-3 text-muted text-xs tracking-[0.15em] uppercase transition-all duration-300 peer-focus:-top-3 peer-focus:text-[10px] peer-[:not(:placeholder-shown)]:-top-3 peer-[:not(:placeholder-shown)]:text-[10px]"
      >
        {label}
      </label>
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte füllen Sie alle Felder aus.");
      return;
    }
    const subject = encodeURIComponent(`Kontaktanfrage von ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nE-Mail: ${form.email}\n\nNachricht:\n${form.message}`
    );
    window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
    toast.success("Ihr E-Mail-Programm wird geöffnet…");
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <section id="contact" className="bg-sand py-24 md:py-36" data-testid="contact">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <p className="text-[11px] tracking-[0.35em] uppercase text-muted mb-6">Kontakt</p>
          <h2 className="font-serif font-light text-5xl md:text-7xl tracking-tight leading-[0.9] mb-10">
            Gemeinsam <span className="italic text-stone">gestalten</span>
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-stone max-w-md mb-12">
            Für Castings, Kampagnen und Kooperationen erreichen Sie uns über das
            Formular unten oder direkt. Alle Anfragen werden von Jannas Elternteil bearbeitet.
          </p>

          <div className="space-y-6">
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-muted mb-1">E-Mail</p>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-link"
                className="font-serif text-2xl md:text-3xl text-charcoal hover:text-stone transition-colors"
              >
                {CONTACT.email}
              </a>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-muted mb-1">Telefon</p>
              <a
                href={`tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`}
                data-testid="contact-phone-link"
                className="font-serif text-2xl md:text-3xl text-charcoal hover:text-stone transition-colors"
              >
                {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          onSubmit={submit}
          className="space-y-10 lg:pt-16"
          data-testid="contact-form"
        >
          <Field id="name" label="Ihr Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field id="email" label="E-Mail-Adresse" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Field id="message" label="Erzählen Sie uns von Ihrem Projekt" textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />

          <button
            type="submit"
            data-testid="contact-submit-button"
            className="group inline-flex items-center gap-3 bg-charcoal text-pearl px-9 py-4 rounded-full text-xs tracking-[0.2em] uppercase transition-all duration-300 hover:bg-stone hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg hover:shadow-charcoal/10"
          >
            Anfrage senden
            <span className="transition-transform duration-300 group-hover:translate-x-2">→</span>
          </button>
        </motion.form>
      </div>
    </section>
  );
}
